import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  addApartments,
  listProperties,
  PropertiesResponse,
  removeProperty,
  StaysProperty,
  updateProperty,
} from '../../../../api/stays-properties-client';
import { StaysApiError } from '../../../../api/stays-client';
import { useAuth } from '../../../../context/auth-provider';
import { StateSwitch } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { Field, FieldError, FieldHint, FieldRow, Input, Label } from '../../../form-kit';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import {
  BuildingLocation,
  BuildingLocationPicker,
  EMPTY_LOCATION,
} from '../../dashboard/dashboard-buildings-components/building-location-picker';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { toast } from '../../../../utils/toast';
import { StaysSectionTabs } from '../stays-section';
import { Quiet, StepTitle, Why } from '../setup/setup-wizard.styled';
import { addressLine, mapFieldErrors, MULTI_MAX, propertyRights, propertySummary, splitPastedList } from './properties-model';
import { HotelRoomsModal, HotelRoomsMode } from './hotel-rooms-modal';
import {
  Actions,
  CardList,
  CardMeta,
  CardTitle,
  Inline,
  Page,
  PageHead,
  PasteArea,
  PropertyCard,
  Small,
  UnitList,
  UnitRow,
} from './properties.styled';

const STATUS_WORDS: Record<string, { tone: 'success' | 'warning' | 'neutral'; label: string }> = {
  active: { tone: 'success', label: 'Open for stays' },
  paused: { tone: 'warning', label: 'Paused' },
  draft: { tone: 'neutral', label: 'Not open yet' },
};

/**
 * YOUR PROPERTIES — /dashboard/stays/properties (Independent Stays IS2).
 * Shown in an organisation without Lobbero Desk, where it takes the place of
 * Buildings and Units. One card per property: its address, its kind, its
 * apartments and their listings, and Edit address / Add apartments / Remove.
 * "Add a property" opens the setup guide at its first step.
 */
export const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  // The server's own guards (BE src/stays/routes/properties.js): a property is a
  // building, so adding, editing and removing one also take the building and
  // unit permissions — an owner's, not a property manager's.
  const { canAdd, canEdit, canRemove } = propertyRights(permissions);
  const [data, setData] = useState<PropertiesResponse | null>(null);
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [editing, setEditing] = useState<StaysProperty | null>(null);
  const [adding, setAdding] = useState<StaysProperty | null>(null);
  const [hotelRooms, setHotelRooms] = useState<HotelRoomsMode>(null);
  const [removing, setRemoving] = useState<StaysProperty | null>(null);
  const [removeProblem, setRemoveProblem] = useState<string | null>(null);

  const load = useCallback((quiet = false) => {
    if (!quiet) setView('loading');
    listProperties()
      .then(d => {
        setData(d);
        setView('ready');
      })
      .catch(() => !quiet && setView('error'));
  }, []);
  useEffect(() => load(), [load]);

  const doRemove = async () => {
    if (!removing) return;
    setRemoveProblem(null);
    try {
      await removeProperty(removing.building_id);
      toast.success(`${removing.name} was removed.`);
      setRemoving(null);
      load(true);
    } catch (e) {
      setRemoveProblem(refusalText(e instanceof StaysApiError ? e : null, 'Could not remove the property. Please try again.'));
    }
  };

  return (
    <Page data-properties-page="">
      <StaysSectionTabs />
      <PageHead>
        <div>
          <StepTitle as="h2">Your properties</StepTitle>
          <Why>Each property is a place guests stay in. Its address sets the time zone of its stays.</Why>
        </div>
        {canAdd ? <AppButton onClick={() => navigate('/dashboard/stays/setup')}>Add a property</AppButton> : null}
      </PageHead>
      <StateSwitch state={view}>
        {view === 'loading' ? (
          <ListSkeleton rows={3} label="your properties" />
        ) : view === 'error' ? (
          <LoadError message="Could not load your properties." onRetry={() => load()} />
        ) : data && data.items.length === 0 ? (
          <div>
            <Quiet>You have no properties yet.</Quiet>
            {canAdd ? (
              <Inline>
                <AppButton onClick={() => navigate('/dashboard/stays/setup')}>Add your first property</AppButton>
              </Inline>
            ) : null}
          </div>
        ) : (
          <CardList aria-label="Your properties">
            {(data ? data.items : []).map(p => (
              <PropertyCard key={p.building_id} data-property={p.name}>
                <div>
                  <CardTitle>{p.name}</CardTitle>
                  <CardMeta>{addressLine(p)}</CardMeta>
                  <CardMeta>
                    {propertySummary(p)} · Times: {p.zone}
                    {p.future_stays ? ` · ${p.future_stays} ${p.future_stays === 1 ? 'stay' : 'stays'} to come` : ''}
                  </CardMeta>
                </div>
                <UnitList aria-label={`Listings at ${p.name}`}>
                  {(p.layout === 'hotel' ? hotelOrder(p) : p.units).map(u => (
                    <UnitRow key={u.unit_id}>
                      {u.listing ? (
                        <Link to={`/dashboard/stays/${u.listing.id}`}>
                          {u.listing.display_name}
                          {p.layout === 'multi' ? ` (${u.label})` : ''}
                          {p.layout === 'hotel' ? hotelRoomSuffix(p, u.listing, u.label) : ''}
                        </Link>
                      ) : (
                        <span>{u.label}</span>
                      )}
                      {u.listing && STATUS_WORDS[u.listing.status] ? (
                        <StatusPill tone={STATUS_WORDS[u.listing.status].tone} dot>
                          {STATUS_WORDS[u.listing.status].label}
                        </StatusPill>
                      ) : null}
                    </UnitRow>
                  ))}
                </UnitList>
                {canEdit || canAdd || canRemove ? (
                  <Actions>
                    {canEdit ? (
                    <AppButton variant="secondary" size="compact" onClick={() => setEditing(p)} aria-label={`Edit the address of ${p.name}`}>
                      Edit address
                    </AppButton>
                    ) : null}
                    {canAdd && p.layout === 'hotel' ? (
                      <>
                        <AppButton
                          variant="secondary"
                          size="compact"
                          onClick={() => setHotelRooms({ property: p, kind: 'rooms' })}
                          disabled={!(p.room_types || []).length}
                          aria-label={`Add rooms to ${p.name}`}
                        >
                          Add rooms
                        </AppButton>
                        <AppButton
                          variant="secondary"
                          size="compact"
                          onClick={() => setHotelRooms({ property: p, kind: 'type' })}
                          aria-label={`Add a room type to ${p.name}`}
                        >
                          Add a room type
                        </AppButton>
                      </>
                    ) : null}
                    {canAdd && p.layout === 'multi' ? (
                      <AppButton variant="secondary" size="compact" onClick={() => setAdding(p)} aria-label={`Add apartments to ${p.name}`}>
                        Add apartments
                      </AppButton>
                    ) : null}
                    {canRemove ? (
                      <AppButton
                        variant="ghost"
                        size="compact"
                        onClick={() => {
                          setRemoveProblem(null);
                          setRemoving(p);
                        }}
                        aria-label={`Remove ${p.name}`}
                      >
                        Remove property
                      </AppButton>
                    ) : null}
                  </Actions>
                ) : null}
              </PropertyCard>
            ))}
          </CardList>
        )}
      </StateSwitch>

      <EditAddressModal
        property={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load(true);
        }}
      />
      <HotelRoomsModal
        mode={hotelRooms}
        onClose={() => setHotelRooms(null)}
        onSaved={() => {
          setHotelRooms(null);
          load(true);
        }}
      />
      <AddApartmentsModal
        property={adding}
        onClose={() => setAdding(null)}
        onSaved={() => {
          setAdding(null);
          load(true);
        }}
      />
      <ConfirmDialog
        open={!!removing}
        title={removing ? `Remove ${removing.name}?` : 'Remove the property?'}
        message={
          removing && removing.future_stays
            ? `This property has ${removing.future_stays} ${removing.future_stays === 1 ? 'stay' : 'stays'} still to come. Cancel or move them first.`
            : 'Its listings close and it leaves your properties. Past stays are kept.'
        }
        primaryLabel={removing ? `Confirm: remove ${removing.name}` : 'Remove'}
        primaryVariant="destructive"
        onPrimary={doRemove}
        dismissLabel="Keep it"
        onDismiss={() => setRemoving(null)}
      >
        {removeProblem ? <FieldError id="property-remove-problem" message={removeProblem} /> : null}
      </ConfirmDialog>
    </Page>
  );
};

/* ── Edit the name, address and pin ── */

const toLocation = (p: StaysProperty): BuildingLocation => ({
  ...EMPTY_LOCATION,
  latitude: p.latitude,
  longitude: p.longitude,
  formatted_address: p.formatted_address,
  address: p.address,
  city: p.city,
  country: p.country,
  country_code: p.country_code,
});

const EditAddressModal: React.FC<{ property: StaysProperty | null; onClose: () => void; onSaved: () => void }> = ({
  property,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [location, setLocation] = useState<BuildingLocation>(EMPTY_LOCATION);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [zoneAsk, setZoneAsk] = useState<{ from: string; to: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!property) return;
    setName(property.name);
    setAddress(property.address || '');
    setCity(property.city || '');
    setLocation(toLocation(property));
    setErrors({});
    setProblem(null);
    setZoneAsk(null);
  }, [property]);

  const save = async (confirm: boolean) => {
    if (!property) return;
    setSaving(true);
    setErrors({});
    setProblem(null);
    const moved = location.latitude !== property.latitude || location.longitude !== property.longitude
      || location.country_code !== property.country_code;
    try {
      await updateProperty(property.building_id, {
        name: name.trim(),
        address: address.trim(),
        city: city.trim(),
        ...(moved
          ? {
              location: {
                latitude: location.latitude,
                longitude: location.longitude,
                country_code: location.country_code,
                country: location.country,
                formatted_address: location.formatted_address,
              },
            }
          : {}),
        ...(confirm ? { confirm_zone_change: true } : {}),
      });
      toast.success(`${name.trim() || property.name} was saved.`);
      onSaved();
    } catch (e) {
      const se = e instanceof StaysApiError ? e : null;
      if (se && se.code === 'ZONE_CHANGE_NEEDS_CONFIRM') {
        const tz = se.extra.timezone as { from: string; to: string } | undefined;
        setZoneAsk(tz ? { from: tz.from, to: tz.to } : { from: property.zone, to: '' });
      } else if (se && se.code === 'INVALID_FIELDS') setErrors(mapFieldErrors(se.fields));
      else setProblem(refusalText(se, 'Could not save the property. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open={!!property}
      title={property ? `Edit ${property.name}` : 'Edit the property'}
      onClose={onClose}
      footer={
        zoneAsk ? (
          <Inline>
            <AppButton variant="secondary" onClick={() => setZoneAsk(null)}>
              Keep editing
            </AppButton>
            <AppButton onClick={() => save(true)} loading={saving} loadingLabel="Saving…">
              Confirm: move to {zoneAsk.to || 'the new time zone'}
            </AppButton>
          </Inline>
        ) : (
          <Inline>
            <AppButton variant="secondary" onClick={onClose}>
              Cancel
            </AppButton>
            <AppButton onClick={() => save(false)} loading={saving} loadingLabel="Saving…">
              Save
            </AppButton>
          </Inline>
        )
      }
    >
      <Field>
        <Label htmlFor="edit-property-name">Name</Label>
        <Input id="edit-property-name" value={name} maxLength={120} onChange={e => setName(e.target.value)} />
        <FieldError id="edit-property-name-error" message={errors.name} />
      </Field>
      <BuildingLocationPicker value={location} onChange={setLocation} invalid={!!errors.location} manualFields={false} />
      <FieldError id="edit-property-location-error" message={errors.location} />
      <FieldRow>
        <Field>
          <Label htmlFor="edit-property-address">Street address</Label>
          <Input id="edit-property-address" value={address} maxLength={200} onChange={e => setAddress(e.target.value)} />
          <FieldError id="edit-property-address-error" message={errors.address} />
        </Field>
        <Field>
          <Label htmlFor="edit-property-city">Town or city</Label>
          <Input id="edit-property-city" value={city} maxLength={80} onChange={e => setCity(e.target.value)} />
          <FieldError id="edit-property-city-error" message={errors.city} />
        </Field>
      </FieldRow>
      {zoneAsk ? (
        <FieldHint role="status">
          This moves the property from {zoneAsk.from} to {zoneAsk.to || 'another time zone'}. Every time shown for it moves too.
        </FieldHint>
      ) : property ? (
        <Small>Times for this property: {property.zone}</Small>
      ) : null}
      {problem ? <FieldError id="edit-property-problem" message={problem} /> : null}
    </FormModal>
  );
};

/* ── A hotel's rooms, grouped by room type (IS9) ── */

const roomTypeName = (p: StaysProperty, id: string | null | undefined): string => {
  const rt = (p.room_types || []).find(x => x.id === id);

  return rt ? rt.name : 'No room type';
};

// A room keeps its type's name as its listing name ("Double"), so the line adds
// the room number ("Double 101"); a room the host renamed shows its number and type.
const hotelRoomSuffix = (p: StaysProperty, listing: { display_name: string; room_type_id?: string | null }, label: string) => {
  const type = roomTypeName(p, listing.room_type_id);

  return listing.display_name === type ? ` ${label}` : ` (${label}) · ${type}`;
};

const hotelOrder = (p: StaysProperty) => {
  const sortOf = (id: string | null | undefined) => {
    const rt = (p.room_types || []).find(x => x.id === id);

    return rt ? rt.sort : 9999;
  };

  return p.units
    .slice()
    .sort(
      (a, b) =>
        sortOf(a.listing && a.listing.room_type_id) - sortOf(b.listing && b.listing.room_type_id) ||
        roomTypeName(p, a.listing && a.listing.room_type_id).localeCompare(roomTypeName(p, b.listing && b.listing.room_type_id)) ||
        a.label.localeCompare(b.label, undefined, { numeric: true })
    );
};

/* ── Add apartments to several apartments ── */

const AddApartmentsModal: React.FC<{ property: StaysProperty | null; onClose: () => void; onSaved: () => void }> = ({
  property,
  onClose,
  onSaved,
}) => {
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setText('');
    setProblem(null);
  }, [property]);
  const list = splitPastedList(text);

  const save = async () => {
    if (!property || !list.length) return;
    setSaving(true);
    setProblem(null);
    try {
      const out = await addApartments(property.building_id, { units: list.map(label => ({ label })) });
      toast.success(`${out.listing_ids.length} ${out.listing_ids.length === 1 ? 'apartment' : 'apartments'} added to ${property.name}.`);
      onSaved();
    } catch (e) {
      const se = e instanceof StaysApiError ? e : null;
      const first = se && se.code === 'INVALID_FIELDS' ? Object.values(se.fields)[0] : null;
      setProblem(first || refusalText(se, 'Could not add the apartments. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open={!!property}
      title={property ? `Add apartments to ${property.name}` : 'Add apartments'}
      onClose={onClose}
      footer={
        <Inline>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton onClick={save} loading={saving} loadingLabel="Adding…" disabled={!list.length}>
            {list.length ? `Add ${list.length} ${list.length === 1 ? 'apartment' : 'apartments'}` : 'Add apartments'}
          </AppButton>
        </Inline>
      }
    >
      <Field>
        <Label htmlFor="add-apartments-list">Apartment numbers, one per line or separated by commas</Label>
        <PasteArea id="add-apartments-list" value={text} onChange={e => setText(e.target.value)} />
        <FieldHint>
          Up to {MULTI_MAX} at a time. Each gets its own listing with this property&apos;s check-in times.
        </FieldHint>
      </Field>
      {problem ? <FieldError id="add-apartments-problem" message={problem} /> : null}
    </FormModal>
  );
};
